// src/pages/contact/page.tsx
import React from "react";
import { Header } from "../../components/feature/Header";
import { Footer } from "../../components/feature/Footer";
import PageHero from "../../components/ui/PageHero";
import heroContact from "../../assets/contact-hero.jpg";

type State = { ok: true } | { ok: false; error?: string } | null;

export default function ContactPage() {
  const [state, setState] = React.useState<State>(null);
  const [sending, setSending] = React.useState(false);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setState(null);
    setSending(true);

    try {
      const form = e.currentTarget;
      const data = new FormData(form);
      // Honeypot simple (bot)
      if ((data.get("website") as string)?.trim()) {
        throw new Error("Anti-spam");
      }

      const res = await fetch("/api/contact.php", {
        method: "POST",
        body: data,
        credentials: "same-origin",
      });

      // On essaie de lire la réponse JSON du PHP
      let json: any = null;
      try {
        json = await res.json();
      } catch {
        // si le PHP renvoie une page vide, on se base sur le status HTTP
        json = { ok: res.ok };
      }

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `Erreur serveur (${res.status})`);
      }

      setState({ ok: true });
      form.reset();
    } catch (err: any) {
      setState({ ok: false, error: err?.message || "Envoi impossible" });
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <Header />

      <PageHero
        src={heroContact}
        title="Contactez-nous"
        subtitle="Nous répondons rapidement à vos messages."
        mode="contain-blur"
        heightClass="h-[44vh] md:h-[56vh]"
        focal="50% 45%"
        alt="CSVBV - Contact"
      />

      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
            <form onSubmit={handleSubmit} id="contact-csvbv" className="space-y-5">
              {/* Champ honeypot anti-spam (ne pas supprimer) */}
              <input name="website" type="text" tabIndex={-1} autoComplete="off" className="hidden" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Nom
                  </label>
                  <input
                    id="name"
                    name="name"
                    required
                    placeholder="Votre nom"
                    className="mt-1 w-full rounded-lg border border-gray-300 p-3 focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    E-mail
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="vous@exemple.fr"
                    className="mt-1 w-full rounded-lg border border-gray-300 p-3 focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                  Sujet (facultatif)
                </label>
                <input
                  id="subject"
                  name="subject"
                  placeholder="Sujet de votre message"
                  className="mt-1 w-full rounded-lg border border-gray-300 p-3 focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  placeholder="Votre message…"
                  className="mt-1 w-full rounded-lg border border-gray-300 p-3 h-40 resize-y focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>

              <div className="flex items-center justify-between gap-4">
                <p className="text-xs text-gray-500">Nous vous répondrons par e-mail.</p>
                <button
                  type="submit"
                  disabled={sending}
                  className={`px-6 py-3 rounded-lg text-white ${
                    sending ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  {sending ? "Envoi…" : "Envoyer"}
                </button>
              </div>

              <div aria-live="polite">
                {state?.ok && (
                  <p className="mt-2 text-green-600 text-sm">Message envoyé ✅</p>
                )}
                {state?.ok === false && (
                  <p className="mt-2 text-red-600 text-sm">
                    Oups, échec de l’envoi. {state.error ? `(${state.error})` : "Réessayez plus tard."}
                  </p>
                )}
              </div>
            </form>
          </div>

          <div className="text-center mt-8 text-sm text-gray-500">
            Ou écrivez-nous :{" "}
            <a className="text-blue-600 hover:underline" href="mailto:contact@volleyball-viarmes.fr">
              contact@volleyball-viarmes.fr
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
